/**
 * frappe-ui useCall expects `{ data: T }` but classic Frappe RPC returns `{ message: T }`.
 * Normalize successful /api responses so useCall, resources, etc. work against a real site.
 *
 * Also implements the single-flight 401 → refresh → retry-once flow for expired JWTs
 * (FRONTEND_BRIEF.md section 5). The refresh call itself is issued with
 * `originalFetch` and NO Authorization header, because the backend JWT auth hook
 * rejects an expired Bearer with 401 before the guest-allowed refresh endpoint runs.
 */
import { tokenStorage } from './session'
import { resolveSiteName } from './config'
import { messageFromBody } from './api'
import { clearClientCache } from './composables/cacheLifecycle'

const originalFetch = window.fetch.bind(window)

// Auth endpoints that must never be auto-retried after a 401:
// they are the auth flow itself, so retrying them would loop.
const AUTH_METHODS = new Set([
  'glamora.api.auth.login',
  'glamora.api.auth.refresh',
  'glamora.api.auth.logout',
  'glamora.api.auth.logout_all',
  'glamora.api.auth.me',
  'glamora.api.auth.role_home',
  'glamora.api.auth.health',
  'glamora.api.auth.request_otp',
  'glamora.api.auth.verify_otp',
  'glamora.api.auth.verify_email',
  'glamora.api.auth.signup_salon',
  'glamora.api.auth.signup_customer',
  'glamora.api.auth.request_password_reset',
  'glamora.api.auth.validate_reset_key',
  'glamora.api.auth.reset_password_with_key',
  'glamora.api.auth.reset_password_with_otp',
  'glamora.api.auth.change_password',
])

let refreshInFlight: Promise<RefreshResult> | null = null

function isApi(url: string): boolean {
  return url.includes('/api/')
}

function isAuthMethod(url: string): boolean {
  for (const method of AUTH_METHODS) {
    if (url.includes(`/api/method/${method}`)) return true
  }
  return false
}

function attachAuth(opts: RequestInit, token: string | null): RequestInit {
  if (!token) return opts
  const headers = new Headers(opts.headers || {})
  headers.set('Authorization', `Bearer ${token}`)
  return { ...opts, headers }
}

/**
 * `ok`: new tokens stored. `rejected`: the server answered with a 4xx (or
 * there is no refresh token), so the session is over. `unreachable`: no usable
 * answer (offline, 5xx, an unreadable or token-less 2xx); the tokens may still
 * be fine, so the caller must not sign the user out.
 */
type RefreshResult = 'ok' | 'rejected' | 'unreachable'

async function refreshTokens(): Promise<RefreshResult> {
  const refreshToken = tokenStorage.refreshToken
  if (!refreshToken) return 'rejected'
  try {
    const res = await originalFetch('/api/method/glamora.api.auth.refresh', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Accept: 'application/json',
        'X-Frappe-Site-Name': resolveSiteName(),
      },
      credentials: 'include',
      body: JSON.stringify({ refresh_token: refreshToken }),
    })
    if (!res.ok) return res.status >= 500 ? 'unreachable' : 'rejected'
    const json = (await res.json()) as { data?: unknown; message?: unknown }
    const payload = (json?.data ?? json?.message ?? json) as {
      access_token?: string
      refresh_token?: string
    }
    // A 2xx without tokens is not a refusal either: nothing proves the session ended.
    if (!payload?.access_token) return 'unreachable'
    tokenStorage.accessToken = payload.access_token
    if (payload.refresh_token) tokenStorage.refreshToken = payload.refresh_token
    return 'ok'
  } catch {
    return 'unreachable'
  }
}

function refreshOnce(): Promise<RefreshResult> {
  if (!refreshInFlight) {
    refreshInFlight = refreshTokens().finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

function redirectToLogin(): void {
  // Session expiry: the same scope-teardown as a logout, before the redirect
  // (`CACHE_AUDIT.md` §5.4, `PLAN_TRACK_C.md` C2.5). A full page load flushes
  // in-memory state but not IndexedDB, so the persistent clears matter here.
  clearClientCache()
  tokenStorage.clear()
  const current =
    window.location.pathname + window.location.search + window.location.hash
  if (current && current !== '/' && !current.startsWith('/login')) {
    window.location.assign(`/login?redirect=${encodeURIComponent(current)}`)
  } else {
    window.location.assign('/login')
  }
}

const TYPE_BY_STATUS: Record<number, string> = {
  401: 'AuthenticationError',
  403: 'PermissionError',
  404: 'DoesNotExistError',
}

function normalizeJsonBody(res: Response, json: unknown): Response {
  if (!json || typeof json !== 'object' || Array.isArray(json)) return res
  const body = json as Record<string, unknown>

  if (!res.ok) {
    // frappe-ui builds its error (and drops the body) only from
    // `{ errors: [{ type, message }] }`. Glamora and classic Frappe refusals
    // carry `{ message }` / `_server_messages` / `exception`, so without this
    // a refused `useCall` / `useList` request would surface as the bare HTTP
    // status text. Keep every original key; add the document-API shape.
    if (Array.isArray(body.errors) && body.errors.length) return res
    const type =
      typeof body.exc_type === 'string' && body.exc_type
        ? body.exc_type
        : (TYPE_BY_STATUS[res.status] ?? 'Error')
    body.errors = [
      { type, message: messageFromBody(body) ?? '', indicator: 'red' },
    ]
    return new Response(JSON.stringify(body), {
      status: res.status,
      statusText: res.statusText,
      headers: res.headers,
    })
  }

  // frappe-ui reads `{ data }`; classic Frappe answers `{ message }`.
  if (body.message !== undefined && body.data === undefined) {
    body.data = body.message
    return new Response(JSON.stringify(body), {
      status: res.status,
      statusText: res.statusText,
      headers: res.headers,
    })
  }
  return res
}

async function normalizeApiResponse(
  res: Response,
  url: string,
): Promise<Response> {
  if (!isApi(url)) return res
  const ctype = res.headers.get('content-type') || ''
  if (!ctype.includes('application/json')) return res
  try {
    // Must be awaited: normalising a pending Promise changes nothing, which
    // left every `{ message }` answer unread by useCall (null data).
    const json: unknown = await res.clone().json()
    return normalizeJsonBody(res, json)
  } catch {
    return res
  }
}

async function frappeCompatFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const opts: RequestInit = init ? { ...init } : {}
  // Always send cookies to the proxied Frappe backend
  if (opts.credentials == null) {
    opts.credentials = 'include'
  }

  const url =
    typeof input === 'string'
      ? input
      : input instanceof Request
        ? input.url
        : input instanceof URL
          ? input.href
          : String(input)

  // A new tab or a restored browser has the refresh token (localStorage) but
  // no access token (sessionStorage): get one before the first protected call,
  // so a still-valid sign-in is resumed instead of asking for the password.
  if (
    isApi(url) &&
    !isAuthMethod(url) &&
    !tokenStorage.accessToken &&
    tokenStorage.refreshToken
  ) {
    const restored = await refreshOnce()
    if (restored === 'unreachable') throw new TypeError('Failed to fetch')
    if (restored === 'rejected') tokenStorage.clear()
  }

  // Only attach the Bearer token to backend API calls (same as before).
  const authToken = isApi(url) ? tokenStorage.accessToken : null

  const firstRes = await originalFetch(input, attachAuth(opts, authToken))

  // Expired access token on a protected endpoint: refresh once, then retry once.
  if (isApi(url) && firstRes.status === 401 && !isAuthMethod(url)) {
    const refreshed = await refreshOnce()
    if (refreshed === 'ok') {
      const retryRes = await originalFetch(
        input,
        attachAuth(opts, tokenStorage.accessToken),
      )
      return await normalizeApiResponse(retryRes, url)
    }
    if (refreshed === 'unreachable') {
      // Could not ask the server whether the session is still good: keep the
      // tokens and the page, and fail like any other request that got no answer.
      throw new TypeError('Failed to fetch')
    }
    redirectToLogin()
  }

  return await normalizeApiResponse(firstRes, url)
}

/**
 * frappe-ui's `useList` / `useCall` (vueuse `useFetch`) abort the request in
 * flight whenever their URL changes (dependent filters, search, paging) and
 * then record the aborted request's rejection as `error` *after* the new
 * request has cleared it, so a list that loaded fine shows "signal is aborted
 * without reason". A superseded request has no result to report, so it is left
 * pending: the replacement request owns the state. Every other failure, and
 * aborts outside `/api/v2/`, are unchanged.
 */
window.fetch = async function frappeCompatFetchGuarded(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  try {
    return await frappeCompatFetch(input, init)
  } catch (e) {
    const supersededV2 =
      (e as { name?: string } | null)?.name === 'AbortError' &&
      init?.signal?.aborted === true &&
      (typeof input === 'string' ? input : '').includes('/api/v2/')
    if (supersededV2) return new Promise<Response>(() => {})
    throw e
  }
}
